// Comms final pass (1.12.217-beta): Communications settings > Group communications.
//   * Gated Group Communications (default OFF): the first staff member to engage a conversation owns it.
//   * Time-of-day greeting cut-offs for the template "Time-of-day greeting" source (defaults 12:00 and 17:00).
// Stored per company in org_comms_group_settings (migration 284). Only an Owner / Tenant Admin may change it; the database
// enforces that too. It saves on its own button, because it lives in its own table.
import React, { useEffect, useId, useState } from 'react';
import { SupportWriteGuard } from '../../context/SupportSessionContext';
import {
    DEFAULT_GREETING_HOURS,
    GATED_SETTING_DESCRIPTION,
    GATED_SETTING_LABEL,
    GATED_SETTING_PRIVACY_NOTE,
    loadGroupSettings,
    saveGroupSettings,
    validateGreetingHours,
} from '../../lib/conversationGate';

const HOURS = Array.from({ length: 23 }, (_, index) => index + 1);
const hourLabel = (hour) => `${String(hour).padStart(2, '0')}:00`;

export default function GroupCommunicationsSettings({ client, orgId, canChange = false, theme = 'light', onSaved }) {
    const dark = theme === 'dark';
    const descriptionId = useId();
    const noteId = useId();
    const [loaded, setLoaded] = useState(null);
    const [draft, setDraft] = useState({ gatedGroupComms: false, greetingAfternoonHour: DEFAULT_GREETING_HOURS.afternoon, greetingEveningHour: DEFAULT_GREETING_HOURS.evening });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');

    useEffect(() => {
        let active = true;
        loadGroupSettings(client, orgId).then((row) => {
            if (!active) return;
            setLoaded(row);
            setDraft({ gatedGroupComms: row.gatedGroupComms, greetingAfternoonHour: row.greetingAfternoonHour, greetingEveningHour: row.greetingEveningHour });
            if (row.error) setError(row.error);
        });
        return () => { active = false; };
    }, [client, orgId]);

    const hoursProblem = validateGreetingHours(draft.greetingAfternoonHour, draft.greetingEveningHour);
    const changed = loaded && (loaded.gatedGroupComms !== draft.gatedGroupComms
        || loaded.greetingAfternoonHour !== Number(draft.greetingAfternoonHour)
        || loaded.greetingEveningHour !== Number(draft.greetingEveningHour));
    const disabled = !canChange || saving || !loaded || loaded.available === false;
    const labelCls = dark ? 'text-gray-200' : 'text-gray-800';
    const hintCls = dark ? 'text-gray-300' : 'text-gray-600';
    const errorCls = dark ? 'text-red-300' : 'text-red-700';
    const selectCls = dark ? 'bg-gray-800 border-gray-600 text-gray-100' : 'bg-white border-gray-300 text-gray-900';

    const save = async () => {
        setSaving(true);
        setError('');
        setNotice('');
        try {
            await saveGroupSettings(client, { orgId, ...draft });
            const next = {
                ...loaded,
                gatedGroupComms: draft.gatedGroupComms,
                greetingAfternoonHour: Number(draft.greetingAfternoonHour),
                greetingEveningHour: Number(draft.greetingEveningHour),
            };
            setLoaded(next);
            setNotice('Group communications saved.');
            onSaved?.(next);
        } catch (failure) {
            setError(failure.message || 'Could not save Group communications.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="space-y-3" data-group-communications-settings="true">
            {loaded?.available === false && (
                <p className={`text-xs ${hintCls}`} role="note">Group communications will be available after this release's database update is applied.</p>
            )}
            <label className={`flex items-start justify-between gap-3 text-sm ${labelCls}`}>
                <span>
                    <span className="font-medium">{GATED_SETTING_LABEL}</span>
                    <span id={descriptionId} className={`mt-0.5 block text-xs font-normal ${hintCls}`}>{GATED_SETTING_DESCRIPTION}</span>
                    <span id={noteId} className={`mt-1 block text-xs font-normal ${hintCls}`}>{GATED_SETTING_PRIVACY_NOTE}</span>
                </span>
                <input
                    type="checkbox"
                    role="switch"
                    checked={draft.gatedGroupComms}
                    aria-checked={draft.gatedGroupComms}
                    disabled={disabled}
                    onChange={(event) => setDraft((current) => ({ ...current, gatedGroupComms: event.target.checked }))}
                    aria-label={GATED_SETTING_LABEL}
                    aria-describedby={`${descriptionId} ${noteId}`}
                    data-gated-group-toggle="true"
                />
            </label>
            <fieldset className={`space-y-2 text-sm ${labelCls}`}>
                <legend className="font-medium">Template greeting times</legend>
                <p className={`text-xs ${hintCls}`}>
                    The "Time-of-day greeting" template value says Good morning, Good afternoon or Good evening in the company's time zone.
                </p>
                <div className="flex flex-wrap items-center gap-3">
                    <label className="flex items-center gap-2 text-xs">
                        <span>Afternoon starts</span>
                        <select
                            value={draft.greetingAfternoonHour}
                            disabled={disabled}
                            onChange={(event) => setDraft((current) => ({ ...current, greetingAfternoonHour: Number(event.target.value) }))}
                            className={`rounded border px-2 py-1 text-xs ${selectCls}`}
                            aria-label="Afternoon greeting starts at"
                        >
                            {HOURS.slice(0, 22).map((hour) => <option key={hour} value={hour}>{hourLabel(hour)}</option>)}
                        </select>
                    </label>
                    <label className="flex items-center gap-2 text-xs">
                        <span>Evening starts</span>
                        <select
                            value={draft.greetingEveningHour}
                            disabled={disabled}
                            onChange={(event) => setDraft((current) => ({ ...current, greetingEveningHour: Number(event.target.value) }))}
                            className={`rounded border px-2 py-1 text-xs ${selectCls}`}
                            aria-label="Evening greeting starts at"
                        >
                            {HOURS.slice(1).map((hour) => <option key={hour} value={hour}>{hourLabel(hour)}</option>)}
                        </select>
                    </label>
                </div>
                {hoursProblem && <p className={`text-xs ${errorCls}`} role="alert">{hoursProblem}</p>}
            </fieldset>
            {!canChange && <p className={`text-xs ${hintCls}`}>Only an Owner or Tenant Admin can change Group communications.</p>}
            <div className="flex flex-wrap items-center justify-end gap-2">
                {notice && <span className={`text-xs ${hintCls}`} role="status">{notice}</span>}
                {canChange && (
                    <SupportWriteGuard mode="disable" tooltip="Saving is disabled in a read-only support session">
                        <button
                            type="button"
                            onClick={save}
                            disabled={disabled || !changed || Boolean(hoursProblem)}
                            className="ia-btn3d ia-btn3d--go"
                            data-save-group-communications="true"
                        >
                            {saving ? 'Saving…' : 'Save group communications'}
                        </button>
                    </SupportWriteGuard>
                )}
            </div>
            {error && <p className={`text-xs ${errorCls}`} role="alert">{error}</p>}
        </div>
    );
}
